// Progress.jsx

import React from 'react';

// ReactDOM for createPortal
import ReactDOM from 'react-dom'; 

// Context
import { useUI } from 'context/UIContext';
import { useProgress } from 'context/ProgressContext';
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Circle Loader
import { CircleLoader } from 'react-spinners';

// CSS
import './Progress.css';

/**
 * Progress Component
 * 
 * This component renders a masked progress indicator with conditional styles.
 * 
 * @returns {ReactNode} The rendered component.
 */
const Progress = () => {

  // Global Context
  const { background } = useUI();
  const {
    progressMessage,
    progressDetails,
    progressVisible,
  } = useProgress();

  // User Context
  const { user } = useUser();
  
  // Theme Context
  const { theme } = useTheme();

  if (!progressVisible) return null;

  const overlayStyle = {
    backgroundColor: theme.overlay,
    backgroundImage: user
      ? `url(${user.wallpaperDark || 'none'})`
      : `url(${background})`, // Fallback if user is null
    backgroundSize: 'cover', // Ensures the image covers the entire screen
    backgroundRepeat: 'no-repeat', // Prevents the image from repeating
    backgroundAttachment: 'fixed', // Fixes the image to the screen, not the scrolling content
    height: '100vh', // Ensures it takes up the full viewport height
    width: '100vw', // Ensures it takes up the full viewport width
    left: 0,
    opacity: 1,
    position: 'fixed',
    top: 0,
    zIndex: 2000, // Behind the content
  };

  const content = (
    <>
      {/* MASK */}
      <div
        className="progress-mask"
        style={{
          ...overlayStyle, // Gradient image background
        }}
      ></div>

      {/* CONTENT */}
      <div
        className="progress-content"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 2001, // Above the mask
        }}>

        {/* CIRCLE LOADER */}
        <div className="progress-circle">
          <CircleLoader
            size={40}
            color={theme.foregroundMuted}
            loading={true}
          />
        </div>

        {/* MESSAGE */}
        <div
          className="progress-status-message"
          style={{
            color: theme.foreground,
            textAlign: 'center',
            marginBottom: '1rem',
          }}
        >
          {progressMessage}
        </div>

        {/* DETAILS */}
        <div
          className="progress-status-details"
          style={{
            color: theme.foreground,
            textAlign: 'center',
            marginBottom: '1rem',
          }}
        >
          {progressDetails}
        </div>

      </div>
    </>
  );
  
    // Use createPortal to render modalContent outside of the root hierarchy
    return ReactDOM.createPortal(content, document.getElementById('portal-root'));
};

export default Progress;
